"use client";

import { LocaleLink } from "@i18n/routing";
import { Button } from "@ui/components/button";
import { motion } from "framer-motion";
import { ArrowRightIcon } from "lucide-react";
import { useTranslations } from "next-intl";

export const CTASection = () => {
	const t = useTranslations();

	const containerVariants = {
		hidden: { opacity: 0 },
		visible: {
			opacity: 1,
			transition: {
				when: "beforeChildren",
				staggerChildren: 0.2,
			},
		},
	};

	const itemVariants = {
		hidden: { opacity: 0, y: 20 },
		visible: {
			opacity: 1,
			y: 0,
			transition: { type: "spring", stiffness: 100 },
		},
	};

	return (
		<section className="py-16 md:py-24">
			<div className="container relative z-20 mx-auto px-4 max-w-6xl">
				<motion.div
					className="text-center max-w-4xl mx-auto"
					variants={containerVariants}
					initial="hidden"
					animate="visible"
				>
					<motion.h2
						className="text-4xl md:text-5xl font-bold mb-6"
						variants={itemVariants}
					>
						<span className="text-foreground">
							{t("common.footer.cta.title.readyToCreate")}{" "}
						</span>
						<span className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 bg-clip-text text-transparent">
							{t("common.footer.cta.title.imagePrompt")}
						</span>
						<span className="text-foreground">?</span>
					</motion.h2>

					<motion.p
						className="text-xl text-slate-600 mb-8"
						variants={itemVariants}
					>
						{t("common.footer.cta.description.joinThousands")}
						<span className="text-indigo-600 font-medium">
							{" "}
							{t("common.footer.cta.description.engaging")}{" "}
						</span>
						{t("common.footer.cta.description.contentFor")}
					</motion.p>

					<motion.div
						className="flex flex-col sm:flex-row justify-center gap-6 items-center mb-12"
						variants={itemVariants}
					>
						<div className="flex items-center">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-6 w-6 text-indigo-500 mr-2"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								aria-hidden="true"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"
								/>
							</svg>
							<span className="text-lg text-slate-600">
								{t("common.footer.cta.features.noCreditCard")}
							</span>
						</div>
						<div className="flex items-center">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								className="h-6 w-6 text-indigo-500 mr-2"
								fill="none"
								viewBox="0 0 24 24"
								stroke="currentColor"
								aria-hidden="true"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7"
								/>
							</svg>
							<span className="text-lg text-slate-600">
								{t(
									"common.footer.cta.features.freeHighQuality",
								)}
							</span>
						</div>
					</motion.div>

					<motion.div
						className="mt-16 flex items-center justify-center"
						variants={itemVariants}
						whileHover={{ scale: 1.05 }}
						whileTap={{ scale: 0.95 }}
					>
						<Button
							size="lg"
							className="bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600 hover:from-blue-600 hover:via-indigo-600 hover:to-purple-700 text-white border-0"
							asChild
						>
							<LocaleLink href="/">
								{t("common.footer.cta.button.tryNow")}
								<motion.span
									initial={{ x: 0 }}
									animate={{ x: [0, 5, 0] }}
									transition={{
										repeat: Number.POSITIVE_INFINITY,
										repeatType: "loop",
										duration: 1.5,
										repeatDelay: 2,
									}}
								>
									<ArrowRightIcon className="ml-2 h-4 w-4" />
								</motion.span>
							</LocaleLink>
						</Button>
					</motion.div>
				</motion.div>
			</div>
		</section>
	);
};
